/**
 * Произвольные квитанции (членские / целевые / прочее) — разметка 1:1 с legacy
 * admin_custom_invoices.tpl.
 *
 * Inline-JS шаблона перенесён в обработчики React с сохранением поведения:
 *  - «Выбрано участков: N» + модал «Список участков» (общий компонент components/Modal.tsx:
 *    закрытие по фону/Esc; все участки отмечены по умолчанию, кнопки «Выбрать все» /
 *    «Убрать все» — legacy select_all());
 *  - блок .values (период / назначение / сумма / рассылка / кнопка) скрыт, пока тип
 *    не выбран; для типа «Прочее» (4) поле «Период» не показывается (closeall + switch);
 *  - цвет <select> серый (#8e8e8e) при «Не выбран», чёрный — иначе;
 *  - в поле суммы допускаются только символы /^[0-9,.]{0,9}$/ (иначе значение откатывается);
 *  - переключатель «Активировать рассылку» показывает .descriptioninform;
 *  - пустой выбор участков → «Выберите абонентов!» (legacy #send_desktop_p), отправки нет.
 *
 * Рассылка защищена одноразовым токеном blocker (GET /api/admin/mailing/token) —
 * это скрытое поле blocker формы шаблона.
 *
 * API: GET /api/admin/invoices/custom (areas), POST /api/admin/invoices/custom
 *      (invoice_type, period, appointment, amount, areas_sel[], inform, blocker).
 * ВНИМАНИЕ: бекенд считает рассылку включённой по САМОМУ ФАКТУ наличия поля inform
 * (Invoices::createCustom: $request->post('inform') !== null) — поэтому, когда
 * переключатель выключен, поле inform в тело запроса не кладём вовсе.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api, ApiError } from '@/api/client'
import { Modal } from '@/components/Modal'
import { useApi } from '@/lib/useApi'

/** Участок (t_area) — Invoices::customList → 'areas'. */
interface AreaRow {
  id: number
  name: string | null
  /** ФИО собственника — как в списке рассылки. */
  fio?: string
}

interface CustomData {
  areas: AreaRow[]
}

interface CreateResult {
  created: number
  wasSend: boolean
  wasAdd: boolean
}

interface TokenResponse {
  blocker: string
}

/** admin.css/newapproach.css — так же, как их подключает admin.tpl (в index.html их нет). */
function useAdminCss(): void {
  useEffect(() => {
    for (const href of ['/assets/css/admin.css', '/assets/css/newapproach.css']) {
      if (document.head.querySelector(`link[href="${href}"]`) !== null) continue
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }
  }, [])
}

export function AdminCustomInvoicesPage() {
  useAdminCss()

  const { data } = useApi<CustomData>('/api/admin/invoices/custom')
  const token = useApi<TokenResponse>('/api/admin/mailing/token')

  // legacy: в списке участков показываются только строки с непустым name
  const areas = (data?.areas ?? []).filter((area) => area.name !== null && area.name !== '')

  const [blocker, setBlocker] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [selected, setSelected] = useState<number[]>([])
  const [initialized, setInitialized] = useState(false)

  const [invoiceType, setInvoiceType] = useState('0')
  const [period, setPeriod] = useState('')
  const [appointment, setAppointment] = useState('')
  const [amount, setAmount] = useState('')
  const [inform, setInform] = useState(false)

  const [busy, setBusy] = useState(false)
  const [oks, setOks] = useState<string[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [wasSend, setWasSend] = useState(false)
  const [wasAdd, setWasAdd] = useState(false)

  useEffect(() => {
    if (token.data !== null) setBlocker(token.data.blocker)
  }, [token.data])

  // legacy: все чекбоксы участков отмечены (checked="checked") при загрузке
  useEffect(() => {
    if (data === null || initialized) return
    setSelected(data.areas.filter((area) => area.name !== null && area.name !== '').map((area) => area.id))
    setInitialized(true)
  }, [data, initialized])

  const showValues = invoiceType !== '0'
  const showPeriod = invoiceType === '2' || invoiceType === '3' // тип 4: values_period не показывается

  function toggleArea(id: number): void {
    setSelected((list) => (list.includes(id) ? list.filter((item) => item !== id) : [...list, id]))
  }

  /** legacy: $('.amount_input').on('input') — откат значения, если не подходит под маску. */
  function onAmountInput(value: string): void {
    if (/^[0-9,.]{0,9}$/.test(value)) setAmount(value)
  }

  async function ensureBlocker(): Promise<string> {
    if (blocker !== null) return blocker
    const fresh = await api.get<TokenResponse>('/api/admin/mailing/token')
    setBlocker(fresh.blocker)
    return fresh.blocker
  }

  /** legacy: submit add_invoces (+ проверка «Выберите абонентов!» перед отправкой). */
  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setOks([])
    setErrors([])
    setWasSend(false)
    setWasAdd(false)

    if (selected.length < 1) {
      setErrors(['Выберите абонентов!'])
      return
    }
    if (
      !window.confirm(
        `Выставить квитанции участкам: ${selected.length}?\n` +
          (inform
            ? 'Рассылка включена: письма и уведомления уйдут РЕАЛЬНЫМ жителям СНТ.\n'
            : '') +
          'У участков с автооплатой счёт спишется с баланса сразу.',
      )
    ) {
      return
    }

    setBusy(true)
    try {
      // inform кладём в тело ТОЛЬКО когда переключатель включён (бекенд смотрит на наличие поля)
      const payload: Record<string, unknown> = {
        blocker: await ensureBlocker(),
        invoice_type: invoiceType,
        period,
        appointment,
        amount,
        areas_sel: selected,
      }
      if (inform) payload['inform'] = '1'

      const result = await api.post<CreateResult>('/api/admin/invoices/custom', payload)
      setBlocker(null) // токен одноразовый
      setWasSend(result.wasSend)
      setWasAdd(result.wasAdd)
      setPeriod('')
      setAppointment('')
      setAmount('')
    } catch (e: unknown) {
      setBlocker(null)
      setErrors([e instanceof ApiError ? e.message : 'Не удалось выставить квитанции'])
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <form method="POST" className="add-form" id="form_id" onSubmit={(event) => void submit(event)}>
        <p className="margin_null" style={{ display: 'flex', marginLeft: '0px' }}>
          Выбрано участков:&nbsp;&nbsp;&nbsp;<span id="abon_areas">{selected.length}</span>
          <button
            type="button"
            className="btn btn-default"
            style={{ marginLeft: '0px', position: 'relative', left: 10, top: -5 }}
            onClick={() => setShowModal(true)}
          >
            Изменить
          </button>
        </p>

        <Modal open={showModal} onClose={() => setShowModal(false)} title="Список участков">
          <button
            style={{ transform: 'scale(0.9)', cursor: 'pointer' }}
            type="button"
            className="btn btn-default"
            onClick={() => setSelected(areas.map((area) => area.id))}
          >
            Выбрать все
          </button>
          <button
            style={{ transform: 'scale(0.9)', cursor: 'pointer' }}
            type="button"
            className="btn btn-default"
            onClick={() => setSelected([])}
          >
            Убрать все
          </button>

          {/* Порядок как в списках рассылок: галочка → участок → ФИО. */}
          <table className="table table-hover" style={{ marginTop: 15 }}>
            <thead>
              <tr>
                <th />
                <th className="mob_td_areaname">Участок</th>
                <th className="mob_td_fio">ФИО</th>
              </tr>
            </thead>
            <tbody>
              {areas.map((area) => (
                <tr
                  key={area.id}
                  className="trline"
                  // клик по строке переключает галочку — как в списках рассылок
                  onClick={(e) => {
                    if ((e.target as HTMLElement).tagName === 'INPUT') return
                    toggleArea(area.id)
                  }}
                >
                  <td className="checkbox_modal">
                    <input
                      type="checkbox"
                      name={`areas_sel[${area.id}]`}
                      value={area.id}
                      checked={selected.includes(area.id)}
                      onChange={() => toggleArea(area.id)}
                      className="areas_sel"
                    />
                  </td>
                  <td className="mob_td_areaname">{area.name}</td>
                  <td className="mob_td_fio">{area.fio !== undefined && area.fio !== '' ? area.fio : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>

        <div className="row" style={{ margin: '0px' }}>
          <div className="col-xs-12" style={{ paddingBottom: 10, paddingLeft: 0 }}>
            <div className="informblock">
              {oks.map((text, index) => (
                <p key={index} style={{ color: '#008000' }}>
                  {text}
                </p>
              ))}
              {errors.map((text, index) => (
                <p key={index} style={{ color: '#ce4844' }}>
                  {text}
                </p>
              ))}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 col-sm-8 col-xs-12">
            <div
              className="form-control news-input"
              style={{ paddingBottom: 6, display: 'table', marginBottom: 5 }}
            >
              <span style={{ width: 75, display: 'table-cell', color: 'black' }}>Тип:&nbsp;</span>
              <select
                name="invoice_type"
                className="invoice_type"
                style={{
                  display: 'table-cell',
                  border: 0,
                  outline: 0,
                  width: '100%',
                  color: invoiceType === '0' ? '#8e8e8e' : 'black',
                }}
                value={invoiceType}
                onChange={(event) => setInvoiceType(event.target.value)}
              >
                <option value="0">Не выбран</option>
                <option value="2">Членские взносы</option>
                <option value="3">Целевые взносы</option>
                <option value="4">Прочее</option>
              </select>
            </div>

            {showValues && (
              <div className="values">
                {showPeriod && (
                  <div
                    className="form-control news-input values_period"
                    style={{ paddingBottom: 6, display: 'table', marginBottom: 5 }}
                  >
                    <span style={{ width: 75, display: 'table-cell', color: 'black' }}>Период:&nbsp;</span>
                    <input
                      name="period"
                      className="title_input"
                      style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
                      value={period}
                      onChange={(event) => setPeriod(event.target.value)}
                      placeholder="Оплачиваемый период"
                    />
                  </div>
                )}

                <div
                  className="form-control news-input values_appointment"
                  style={{ paddingBottom: 6, display: 'table', marginBottom: 5 }}
                >
                  <span style={{ width: 75, display: 'table-cell', color: 'black' }}>Назначение:&nbsp;</span>
                  <input
                    name="appointment"
                    className="appointment_input"
                    style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
                    value={appointment}
                    onChange={(event) => setAppointment(event.target.value)}
                    placeholder="Назначение платежа"
                  />
                </div>

                <div
                  className="form-control news-input values_amount"
                  style={{ paddingBottom: 6, display: 'table', marginBottom: 5 }}
                >
                  <span style={{ width: 75, display: 'table-cell', color: 'black' }}>Сумма:&nbsp;</span>
                  <input
                    name="amount"
                    className="amount_input"
                    style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
                    value={amount}
                    onChange={(event) => onAmountInput(event.target.value)}
                    placeholder="Сумма в формате 1000,00"
                  />
                </div>

                <div
                  className="form-control news-input values_inform"
                  style={{ paddingBottom: 6, display: 'table', marginBottom: 5 }}
                >
                  <span style={{ width: 140, display: 'inline-block', color: 'black' }}>
                    Активировать рассылку:&nbsp;
                  </span>
                  <label className="switch">
                    <input
                      type="checkbox"
                      name="inform"
                      className="informcheckbox"
                      checked={inform}
                      onChange={(event) => setInform(event.target.checked)}
                    />
                    <span className="slider" />
                  </label>
                  <div className="descriptioninform">
                    {inform && (
                      <>
                        Рассылка будет произведена по параметрам указанным в настройках участков.{' '}
                        <span style={{ color: '#ce4844' }}>
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <input type="hidden" name="blocker" value={blocker ?? ''} readOnly />
                  <input
                    type="submit"
                    name="add_invoces"
                    className="btn btn-default"
                    value="Выставить квитанции"
                    disabled={busy}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </form>

      {wasSend && (
        <div className="wasSend">
          Квитанции добавлены и разосланы, подробную информацию можно узнать из раздела{' '}
          <Link to="/admin/statistic_log">Статистика</Link>
        </div>
      )}
      {wasAdd && <div className="wasSend">Квитанции добавлены</div>}
    </>
  )
}
